import React from "react";

/**
 * RiskaiΛ brand emblem — plain <img> + mix-blend-mode + radial mask.
 *
 * The supplied PNG has its own dark-navy backdrop baked in. Two clean
 * tricks blend it perfectly into the #1C3A5A page:
 *
 *   1. `mix-blend-mode: lighten` — navy on navy stays navy (i.e. invisible),
 *      while every silver pixel survives at full intensity.
 *   2. `mask-image: radial-gradient(circle at center, black 65%, transparent 100%)`
 *      — physically cuts the square corners off the PNG so any encoding
 *      artifacts at the edges can never produce a visible rectangle.
 *
 * No SVG filter, no chroma-key, no contrast boost — just sharp pixels.
 */
const LOGO_URL =
  "https://customer-assets.emergentagent.com/wingman/368cca20-abd0-4588-99d9-9e42b06e9661/attachments/5a910f3d9a3a4771b1b57a56dc3c7bca_image.png";

const NATIVE_W = 803;
const NATIVE_H = 524;
const ASPECT = NATIVE_H / NATIVE_W;

const EthicsEmblem = ({
  size,
  width,
  height,
  className = "",
  style = {},
  ...rest
}) => {
  const w = width ?? size ?? 320;
  const h = height ?? Math.round(w * ASPECT);

  // Center pulled slightly down so the top edge fades deeper into the
  // background, eliminating the hard rectangular boundary above the badge
  // — same treatment as the sides. Steeper transparency falloff guarantees
  // no visible boundary at any rendered size.
  const maskValue =
    "radial-gradient(ellipse 70% 80% at 50% 56%, black 38%, transparent 92%)";

  return (
    <span
      role="img"
      aria-label="RiskaiΛ — Ethics & Transparency"
      data-riskaia-emblem="true"
      className={className}
      style={{
        width: w,
        height: h,
        display: "inline-block",
        lineHeight: 0,
        userSelect: "none",
        // Hard-reset any inherited container chrome
        background: "transparent",
        border: "none",
        outline: "none",
        padding: 0,
        margin: 0,
        ...style,
      }}
      {...rest}
    >
      <img
        src={LOGO_URL}
        alt=""
        draggable={false}
        style={{
          display: "block",
          width: "100%",
          height: "100%",
          objectFit: "contain",
          mixBlendMode: "lighten",
          // Lift the silver lines + tagline so they read crisply against
          // the #1C3A5A page bg.  Contrast does the heavy lifting — it
          // pushes silver pixels toward white while *crushing* navy darker
          // (so `mix-blend-mode: lighten` keeps the bg fully invisible).
          // A whisper of brightness sharpens highlights without lifting
          // the bg above the page colour.
          filter: "contrast(1.35) brightness(1.08)",
          maskImage: maskValue,
          WebkitMaskImage: maskValue,
          maskMode: "alpha",
          WebkitMaskMode: "alpha",
          maskRepeat: "no-repeat",
          WebkitMaskRepeat: "no-repeat",
          maskSize: "100% 100%",
          WebkitMaskSize: "100% 100%",
          background: "transparent",
          border: "none",
          pointerEvents: "none",
        }}
      />
    </span>
  );
};

export default EthicsEmblem;
